import { useTranslation } from "react-i18next"
import { PackageTabs } from "./PackageTabs"
import { PackageCard } from "./PackageCard"
import { PackageDetail } from "./PackageDetail"
import { deriveOrderAggregateStatus, derivePackageBadgeStatus } from "../utils/packageStatus"
import { statusDotClass, aggregateBadgeClass } from "../utils/statusColors"

const SUMMARY_KEY = 'summary'

export const MultiPackageView = ({ order, activeTab, onTabChange, isEditable = false, isCncEligible = false, onEditAddress, onPickupNearby, onOrderHelp, onViewProofOfDelivery }) => {
    const { i18n } = useTranslation()
    const packages = (order?.packages || []).filter(Boolean)
    const shippingMethodName = order?.shipping_method?.shipping_name
    const carrierNames = [...new Set(packages.map((p) => p?.carrier_name).filter(Boolean))].join(', ')

    const tabs = [
        { key: SUMMARY_KEY, label: i18n.t('multiPackage.resumen_tab') },
        ...packages.map((p, i) => ({
            key: `pkg-${i}`,
            label: i18n.t('multiPackage.package_label', { number: i + 1 }),
            dotClass: statusDotClass(derivePackageBadgeStatus(p)),
        })),
    ]

    const aggregate = deriveOrderAggregateStatus(order)
    // Safe-i18n guard: resolve with an empty defaultValue and only render the pill when the
    // label is non-empty, so a locale missing `aggregate_status.<returned|canceled>` never
    // leaks a raw i18n key (mirrors buildDescription's pattern in PackageDetail).
    const aggregateLabel = i18n.t(`multiPackage.aggregate_status.${aggregate}`, { defaultValue: '' })
    const selectedIndex = activeTab?.startsWith('pkg-') ? Number(activeTab.slice(4)) : null

    return (
        <div>
            <PackageTabs tabs={tabs} activeKey={activeTab} onChange={onTabChange} />
            {activeTab === SUMMARY_KEY ? (
                <div className="flex flex-col gap-3 py-4 px-10 bg-white">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-700">
                            {i18n.t('multiPackage.summary_heading', { count: packages.length, carrier: carrierNames })}
                        </h3>
                        {aggregateLabel && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${aggregateBadgeClass(aggregate)}`}>
                                {aggregateLabel}
                            </span>
                        )}
                    </div>
                    {packages.map((pkg, i) => (
                        <PackageCard
                            key={pkg.shipping_number || i}
                            pkg={pkg}
                            index={i}
                            shippingMethodName={shippingMethodName}
                            onViewTracking={(idx) => onTabChange(`pkg-${idx}`)}
                        />
                    ))}
                </div>
            ) : selectedIndex != null && packages[selectedIndex] ? (
                <div className="py-4 px-10 bg-white">
                    <PackageDetail
                        order={order}
                        pkg={packages[selectedIndex]}
                        index={selectedIndex}
                        isEditable={isEditable}
                        isCncEligible={isCncEligible}
                        onEditAddress={onEditAddress}
                        onPickupNearby={onPickupNearby}
                        onViewProofOfDelivery={onViewProofOfDelivery}
                    />
                </div>
            ) : null}
            {onOrderHelp && (
                <div className="py-4 px-10 bg-white">
                    <button
                        type="button"
                        className="mt-6 text-sm text-blue-500 hover:text-blue-400"
                        onClick={onOrderHelp}
                    >
                        {i18n.t('showOrder.help_with_order')}
                    </button>
                </div>
            )}
        </div>
    )
}
